import { BaseApiService } from './base';
import API_CONFIG, { API_ENDPOINTS } from '../../config/api';
import { buildApiUrl } from '../../utils/apiUrl';

export class SettingsService extends BaseApiService {
  /**
   * Récupère le profil de l'utilisateur connecté
   */
  async getCurrentUserProfile(): Promise<any> {
    try {
      const response = await this.request(API_ENDPOINTS.CURRENT_USER);
      return response;
    } catch (error) {
      console.error('Erreur récupération profil:', error);
      throw error;
    }
  }

  /**
   * Met à jour le profil de l'utilisateur
   */
  async updateUserProfile(profileData: any): Promise<any> {
    try {
      const response = await this.request(API_ENDPOINTS.USER_PROFILE, {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });
      return response;
    } catch (error) {
      console.error('Erreur mise à jour profil:', error);
      throw error;
    }
  }

  /**
   * Récupère la liste de tous les utilisateurs (Admin seulement)
   */
  async getAllUsers(): Promise<any[]> {
    try {
      // Le backend rend soit un tableau, soit une page { count, next, results }.
      // On suit les pages : sept comptes aujourd'hui, mais la liste se
      // tronquerait au vingtieme sans que rien ne le signale.
      return await this.requestList<any>(API_ENDPOINTS.USERS);
    } catch (error) {
      console.error('Erreur récupération utilisateurs:', error);
      throw error;
    }
  }

  /**
   * Crée un nouvel utilisateur (Admin seulement)
   */
  async createUser(userData: any): Promise<any> {
    try {
      const response = await this.request(API_ENDPOINTS.USERS, {
        method: 'POST',
        body: JSON.stringify(userData),
      });
      return response;
    } catch (error) {
      console.error('Erreur création utilisateur:', error);
      throw error;
    }
  }

  /**
   * Met à jour un utilisateur (Admin)
   */
  async updateUser(userId: string, userData: any): Promise<any> {
    try {
      const response = await this.request(`${API_ENDPOINTS.USERS}${userId}/`, {
        method: 'PUT',
        body: JSON.stringify(userData),
      });
      return response;
    } catch (error) {
      console.error('Erreur mise à jour utilisateur:', error);
      throw error;
    }
  }

  /**
   * Active/désactive un utilisateur
   */
  async toggleUserStatus(userId: string): Promise<any> {
    try {
      const response = await this.request(`${API_ENDPOINTS.USERS}${userId}/toggle_active/`, {
        method: 'POST',
      });
      return response;
    } catch (error) {
      console.error('Erreur toggle status:', error);
      throw error;
    }
  }

  /**
   * Réinitialise le mot de passe d'un utilisateur
   */
  async resetUserPassword(userId: string): Promise<{ temporary_password?: string; courriel_envoye?: boolean }> {
    try {
      const response = await this.request<{ temporary_password?: string; courriel_envoye?: boolean }>(`${API_ENDPOINTS.USERS}${userId}/reset_password/`, {
        method: 'POST',
        body: JSON.stringify({ notify: true }),
      });
      return response;
    } catch (error) {
      console.error('Erreur réinitialisation mot de passe:', error);
      throw error;
    }
  }

  /**
   * Récupère les domaines
   */
  async getDomains(): Promise<any[]> {
    try {
      // Liste paginee : on suit les pages, sinon elle se tronque a vingt.
      return await this.requestList<any>(API_ENDPOINTS.DOCUMENT_DOMAINS);
    } catch (error) {
      console.error('Erreur récupération domaines:', error);
      throw error;
    }
  }

  /**
   * Récupère les types de documents
   */
  async getDocumentTypes(domainId?: number): Promise<any[]> {
    try {
      const endpoint = domainId 
        ? `${API_ENDPOINTS.DOCUMENT_TYPES}?domain=${domainId}`
        : API_ENDPOINTS.DOCUMENT_TYPES;
      // Liste paginee : on suit les pages, sinon elle se tronque a vingt.
      return await this.requestList<any>(endpoint);
    } catch (error) {
      console.error('Erreur récupération types documents:', error);
      throw error;
    }
  }

  /**
   * Récupère les sous-types de documents
   */
  async getDocumentSubtypes(documentTypeId?: number): Promise<any[]> {
    try {
      const endpoint = documentTypeId
        ? `${API_ENDPOINTS.DOCUMENT_SUBTYPES}?document_type=${documentTypeId}`
        : API_ENDPOINTS.DOCUMENT_SUBTYPES;
      // Liste paginee : on suit les pages, sinon elle se tronque a vingt.
      return await this.requestList<any>(endpoint);
    } catch (error) {
      console.error('Erreur récupération sous-types:', error);
      throw error;
    }
  }

  /**
   * Crée un nouveau type de document
   */
  async createDocumentType(typeData: any): Promise<any> {
    try {
      const response = await this.request(API_ENDPOINTS.DOCUMENT_TYPES, {
        method: 'POST',
        body: JSON.stringify(typeData),
      });
      return response;
    } catch (error) {
      console.error('Erreur création type document:', error);
      throw error;
    }
  }

  /**
   * Supprime un type de document
   */
  async deleteDocumentType(typeId: number): Promise<any> {
    try {
      const response = await this.request(`${API_ENDPOINTS.DOCUMENT_TYPES}${typeId}/`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      console.error('Erreur suppression type document:', error);
      throw error;
    }
  }

  /**
   * Supprime un sous-type de document
   */
  async deleteDocumentSubtype(subtypeId: number): Promise<any> {
    try {
      const response = await this.request(`${API_ENDPOINTS.DOCUMENT_SUBTYPES}${subtypeId}/`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      console.error('Erreur suppression sous-type:', error);
      throw error;
    }
  }

  /**
   * Crée un nouveau sous-type de document
   */
  async createDocumentSubtype(typeId: number, subtypeName: string): Promise<any> {
    try {
      const response = await this.request(API_ENDPOINTS.DOCUMENT_SUBTYPES, {
        method: 'POST',
        body: JSON.stringify({
          name: subtypeName,
          document_type_id: typeId,  // Le backend attend document_type_id, pas document_type
          description: `${subtypeName}`
        }),
      });
      return response;
    } catch (error) {
      console.error('Erreur création sous-type:', error);
      throw error;
    }
  }

  // ============================================================================
  // DÉPARTEMENTS
  // ============================================================================

  /**
   * Récupère tous les départements
   */
  async getDepartments(): Promise<any[]> {
    try {
      // Liste paginee : on suit les pages, sinon elle se tronque a vingt.
      return await this.requestList<any>(API_ENDPOINTS.DEPARTMENTS);
    } catch (error) {
      console.error('Erreur récupération départements:', error);
      return [];
    }
  }

  /**
   * Crée un nouveau département
   */
  async createDepartment(data: {
    name: string;
    code?: string;
    description?: string;
    manager_name?: string;
  }): Promise<any> {
    return this.request(API_ENDPOINTS.DEPARTMENTS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Met à jour un département
   */
  async updateDepartment(id: number, data: Partial<any>): Promise<any> {
    return this.request(`${API_ENDPOINTS.DEPARTMENTS}${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Supprime un département
   */
  async deleteDepartment(id: number): Promise<void> {
    await this.request(`${API_ENDPOINTS.DEPARTMENTS}${id}/`, {
      method: 'DELETE',
    });
  }

  /**
   * Active/désactive un département
   */
  async toggleDepartmentStatus(id: number): Promise<any> {
    return this.request(`${API_ENDPOINTS.DEPARTMENTS}${id}/toggle_active/`, {
      method: 'POST',
    });
  }

  // ============================================================================
  // RÔLES & PERMISSIONS
  // ============================================================================

  /**
   * Récupère les rôles
   */
  async getRoles(): Promise<any[]> {
    try {
      // Liste paginee : on suit les pages, sinon elle se tronque a vingt.
      return await this.requestList<any>(API_ENDPOINTS.ROLES);
    } catch (error) {
      console.error('Erreur récupération rôles:', error);
      throw error;
    }
  }

  /**
   * Récupère les permissions
   */
  async getPermissions(): Promise<any[]> {
    try {
      // Liste paginee : on suit les pages, sinon elle se tronque a vingt.
      return await this.requestList<any>(API_ENDPOINTS.PERMISSIONS);
    } catch (error) {
      console.error('Erreur récupération permissions:', error);
      throw error;
    }
  }

  /**
   * Récupère la matrice rôles ↔ capacités
   */
  async getPermissionsMatrix(): Promise<{ roles: any[]; capabilities: any[] }> {
    return this.request(API_ENDPOINTS.PERMISSION_MATRIX);
  }

  /**
   * Met à jour les capacités d'un rôle
   */
  async updateRoleCapabilities(roleId: string | number, capabilityCodes: string[]): Promise<any> {
    return this.request(API_ENDPOINTS.ROLE_CAPABILITIES(roleId.toString()), {
      method: 'POST',
      body: JSON.stringify({ capability_codes: capabilityCodes }),
    });
  }

  /**
   * Définit le rôle principal d'un utilisateur
   */
  async setUserRole(userId: string | number, roleCode: string): Promise<any> {
    return this.request(API_ENDPOINTS.USER_ROLE_SET, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, role_code: roleCode }),
    });
  }

  /**
   * Récupère les capacités du compte courant
   */
  async getMyPermissions(): Promise<any> {
    return this.request(API_ENDPOINTS.MY_PERMISSIONS);
  }

  /**
   * Met à jour les capabilities d'un utilisateur
   * Note: Cette méthode utilise les rôles existants. Pour une gestion fine des permissions,
   * il faudrait créer un endpoint backend dédié pour assigner des capabilities directement.
   */
  async updateUserCapabilities(userId: string | number, capabilityCodes: string[]): Promise<any> {
    try {
      // Récupérer tous les rôles disponibles
      const roles = await this.getRoles();
      
      // Trouver un rôle existant qui correspond exactement aux capabilities demandées
      let matchingRole = roles.find((role: any) => {
        const roleCapabilities = role.capabilities?.map((c: any) => c.code) || role.capabilityCodes || [];
        return roleCapabilities.length === capabilityCodes.length &&
               capabilityCodes.every(code => roleCapabilities.includes(code)) &&
               roleCapabilities.every((code: string) => capabilityCodes.includes(code));
      });

      if (matchingRole) {
        // Si un rôle correspond, l'assigner à l'utilisateur
        return await this.setUserRole(userId, matchingRole.code || matchingRole.id.toString());
      } else {
        // Si aucun rôle ne correspond exactement, utiliser le premier rôle admin ou créer un message d'erreur
        // Pour l'instant, on log une erreur car on ne peut pas créer de rôles dynamiquement
        console.warn('Aucun rôle ne correspond aux capabilities demandées:', capabilityCodes);
        throw new Error('Aucun rôle ne correspond aux permissions demandées. Veuillez créer un rôle avec ces permissions d\'abord.');
      }
    } catch (error) {
      console.error('Erreur mise à jour capabilities utilisateur:', error);
      throw error;
    }
  }

  /**
   * Récupère les connexions cloud de l'utilisateur
   */
  async getCloudConnections(): Promise<any[]> {
    try {
      // Liste paginee : on suit les pages, sinon elle se tronque a vingt.
      return await this.requestList<any>(API_ENDPOINTS.CLOUD_CONNECTIONS);
    } catch (error) {
      console.error('Erreur récupération connexions cloud:', error);
      throw error;
    }
  }

  private buildQueryString(params?: Record<string, any>): string {
    if (!params) return '';
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      query.append(key, String(value));
    });
    const result = query.toString();
    return result ? `?${result}` : '';
  }

  /**
   * Récupère les use case templates avec filtres optionnels
   */
  async getUseCases(filters?: { context?: string; category_code?: string; is_active?: boolean }): Promise<any[]> {
    try {
      const query = this.buildQueryString({
        context: filters?.context,
        category_code: filters?.category_code,
        is_active: typeof filters?.is_active === 'boolean' ? String(filters.is_active) : undefined,
      });
      const endpoint = `${API_ENDPOINTS.USECASE_TEMPLATES}${query}`;
      // Liste paginee : sans suivre les pages, le catalogue s'arretait au
      // vingtieme cas d'usage et les suivants disparaissaient sans erreur.
      return await this.requestList<any>(endpoint);
    } catch (error) {
      console.error('Erreur récupération use cases:', error);
      throw error;
    }
  }

  /**
   * Récupère les catégories de use cases
   */
  async getUseCaseCategories(filters?: { context?: string; is_active?: boolean }): Promise<any[]> {
    try {
      const query = this.buildQueryString({
        context: filters?.context,
        is_active: typeof filters?.is_active === 'boolean' ? String(filters.is_active) : undefined,
      });
      const endpoint = `${API_ENDPOINTS.USECASE_CATEGORIES}${query}`;
      // Liste paginee : on suit les pages, sinon elle se tronque a vingt.
      return await this.requestList<any>(endpoint);
    } catch (error) {
      console.error('Erreur récupération catégories use cases:', error);
      throw error;
    }
  }

  /**
   * Crée une nouvelle catégorie de use case
   */
  async createUseCaseCategory(categoryData: any): Promise<any> {
    try {
      const payload = {
        ...categoryData,
        visible_in: categoryData.visible_in || [],
      };
      return await this.request(API_ENDPOINTS.USECASE_CATEGORIES, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error('Erreur création catégorie use case:', error);
      throw error;
    }
  }

  /**
   * Met à jour une catégorie de use case
   */
  async updateUseCaseCategory(categoryId: number, categoryData: any): Promise<any> {
    try {
      const payload = {
        ...categoryData,
        visible_in: categoryData.visible_in || [],
      };
      return await this.request(API_ENDPOINTS.USECASE_CATEGORY_DETAIL(categoryId), {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error('Erreur mise à jour catégorie use case:', error);
      throw error;
    }
  }

  /**
   * Supprime une catégorie de use case
   */
  async deleteUseCaseCategory(categoryId: number): Promise<void> {
    try {
      await this.request(API_ENDPOINTS.USECASE_CATEGORY_DETAIL(categoryId), {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Erreur suppression catégorie use case:', error);
      throw error;
    }
  }

  /**
   * Crée un nouveau use case
   */
  async createUseCase(useCaseData: any): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('title', useCaseData.title);
      formData.append('description', useCaseData.description);
      formData.append('category', useCaseData.category);
      formData.append('link', useCaseData.link || '');  // Vide pour un assistant intégré
      formData.append('mode', useCaseData.mode || 'lien');
      formData.append('system_prompt', useCaseData.system_prompt || '');
      formData.append('output_format', useCaseData.output_format || 'texte');
      if (useCaseData.is_active !== undefined) {
        formData.append('is_active', useCaseData.is_active.toString());
      }
      if (useCaseData.image) {
        formData.append('image', useCaseData.image);
      }
      
      const token = localStorage.getItem('access_token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
      const response = await fetch(buildApiUrl(API_ENDPOINTS.USECASE_TEMPLATES), {
        method: 'POST',
        headers: headers,
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erreur réponse:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur création use case:', error);
      throw error;
    }
  }

  /**
   * Met à jour un use case
   */
  async updateUseCase(useCaseId: number, useCaseData: any): Promise<any> {
    try {
      const formData = new FormData();
      if (useCaseData.title) formData.append('title', useCaseData.title);
      if (useCaseData.description) formData.append('description', useCaseData.description);
      if (useCaseData.category) formData.append('category', useCaseData.category);
      // Envoyés même vides : passer d'un assistant intégré à un lien doit
      // pouvoir effacer les instructions, et inversement.
      if (useCaseData.link !== undefined) formData.append('link', useCaseData.link || '');
      if (useCaseData.mode) formData.append('mode', useCaseData.mode);
      if (useCaseData.system_prompt !== undefined) formData.append('system_prompt', useCaseData.system_prompt || '');
      if (useCaseData.output_format) formData.append('output_format', useCaseData.output_format);
      if (useCaseData.is_active !== undefined) {
        formData.append('is_active', useCaseData.is_active.toString());
      }
      // Ne envoyer image que si c'est un nouveau fichier (File instance)
      // Ne pas envoyer si c'est une URL string (image existante)
      if (useCaseData.image && useCaseData.image instanceof File) {
        formData.append('image', useCaseData.image);
      }
      if (useCaseData.order !== undefined) formData.append('order', useCaseData.order.toString());
      
      const token = localStorage.getItem('access_token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
      const response = await fetch(buildApiUrl(API_ENDPOINTS.USECASE_TEMPLATE_DETAIL(useCaseId)), {
        method: 'PATCH',
        headers: headers,
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur mise à jour use case:', error);
      throw error;
    }
  }

  /**
   * Supprime un use case
   */
  async deleteUseCase(useCaseId: number): Promise<void> {
    try {
      await this.request(API_ENDPOINTS.USECASE_TEMPLATE_DETAIL(useCaseId), {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Erreur suppression use case:', error);
      throw error;
    }
  }
}

// Instance singleton
export const settingsService = new SettingsService();
